import { useState, useMemo } from 'react';

import { Badge, Collapsible, CollapsibleContent } from '@monksflow/monks-ui';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';

import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';
import type { CategoryId } from '@/shared/constants/category-colors';
import { ToolBadge } from '@/shared/ui/ToolBadge';
import { categoricalBadgeClass } from '@/shared/ui/categoricalBadge';

import type { AgentTreeNodeDto } from '@inference.engine/api-client/web';
import type { ToolConfigScope } from '@/features/admin/model/useAgentConfig/useAgentConfig';
import { getFullyConfiguredWorkspaces } from '@/features/admin/lib/toolConfigCoverage';
import { WorkspaceChips } from '../WorkspaceChips';

import { ToolRow } from './ToolRow';
import { useTranslation } from 'react-i18next';

export function AgentNodeCard({
  node,
  toolConfigScopes,
  allWorkspaces = [],
  isRoot,
  onConfigure,
  hasChildren,
  childrenExpanded,
  onToggleChildren,
  tag,
}: {
  node: AgentTreeNodeDto;
  toolConfigScopes?: Map<string, ToolConfigScope>;
  allWorkspaces?: WorkspaceResponseDto[];
  isRoot: boolean;
  onConfigure: (toolId: string, ownerAgentId: string) => void;
  hasChildren: boolean;
  childrenExpanded: boolean;
  onToggleChildren: () => void;
  tag: CategoryId;
}) {
  const { t } = useTranslation('admin');

  const [toolsExpanded, setToolsExpanded] = useState(true);
  const configurableTools = node.tools.filter((t) => t.isConfigurable);
  const unconfiguredCount = configurableTools.filter((t) => !t.isConfigured).length;
  const hasTools = node.tools.length > 0;

  // Compute aggregate scope status for this node's configurable tools
  const allHaveOrg =
    configurableTools.length > 0 &&
    configurableTools.every((t) => {
      const scope = toolConfigScopes?.get(`${t.toolId}:${t.agentId}`);
      return scope?.hasOrg;
    });
  const nodeConfiguredWorkspaces = useMemo(
    () =>
      getFullyConfiguredWorkspaces(
        configurableTools,
        toolConfigScopes ?? new Map<string, ToolConfigScope>(),
        allWorkspaces,
      ),
    [configurableTools, toolConfigScopes, allWorkspaces],
  );

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      {/* Agent header */}
      <button
        type="button"
        onClick={() => {
          if (hasTools) setToolsExpanded(!toolsExpanded);
          else if (hasChildren) onToggleChildren();
        }}
        className="flex w-full cursor-pointer items-center justify-between px-5 py-4 text-left hover:bg-accent"
      >
        <span className="flex min-w-0 flex-row items-center gap-3">
          <ToolBadge toolId={node.agentId} category={tag} size="md" />

          <span className="flex min-w-0 flex-col">
            <span className="flex flex-row items-center gap-2">
              <span className="truncate text-base font-medium leading-6">{node.agentName}</span>
              {!isRoot && (
                <Badge roundness="full" className={categoricalBadgeClass('blue')}>
                  {t('admin.agentNodeCard.subagent')}
                </Badge>
              )}
            </span>
            <span className="text-xs leading-4 text-muted-foreground">
              {t('admin.agentNodeCard.toolCount', { count: node.tools.length })}
              {node.children.length > 0 &&
                ` · ${t('admin.agentNodeCard.subagentCount', { count: node.children.length })}`}
              {unconfiguredCount > 0 &&
                ` · ${t('admin.agentNodeCard.unconfigured', { unconfiguredCount })}`}
            </span>
          </span>
        </span>

        <span className="flex flex-row items-center gap-2">
          {configurableTools.length > 0 &&
            (unconfiguredCount > 0 ? (
              <Badge variant="destructive" roundness="full">
                {t('admin.agentNodeCard.needsSetup', { count: unconfiguredCount })}
              </Badge>
            ) : allHaveOrg ? (
              <Badge variant="positive" roundness="full">
                {t('admin.agentNodeCard.configured')}
              </Badge>
            ) : nodeConfiguredWorkspaces.length > 0 ? (
              <WorkspaceChips workspaces={nodeConfiguredWorkspaces} color="green" />
            ) : (
              <Badge variant="positive" roundness="full">
                {t('admin.agentNodeCard.configured')}
              </Badge>
            ))}
          {(hasTools || hasChildren) &&
            ((hasTools ? toolsExpanded : childrenExpanded) ? (
              <ChevronDownIcon className="size-5 text-muted-foreground" />
            ) : (
              <ChevronRightIcon className="size-5 text-muted-foreground" />
            ))}
        </span>
      </button>

      {/* Tools list */}
      {hasTools && (
        <Collapsible open={toolsExpanded}>
          <CollapsibleContent>
            <div className="flex flex-col border-t border-border">
              {node.tools.map((tool, i) => (
                <ToolRow
                  key={`${tool.agentId}-${tool.toolId}`}
                  tool={tool}
                  isLast={i === node.tools.length - 1}
                  onConfigure={onConfigure}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Subagent toggle row */}
      {hasChildren && hasTools && (
        <button
          type="button"
          onClick={onToggleChildren}
          className="flex w-full cursor-pointer items-center gap-1.5 border-t border-border px-5 py-2.5 hover:bg-accent"
        >
          {childrenExpanded ? (
            <ChevronDownIcon className="size-4 text-primary" />
          ) : (
            <ChevronRightIcon className="size-4 text-primary" />
          )}
          <span className="text-xs font-medium leading-4 text-primary">
            {t('admin.agentNodeCard.subagentCount', { count: node.children.length })}
          </span>
        </button>
      )}
    </div>
  );
}
